/* eslint-disable no-mixed-operators */
/* eslint-disable import/no-named-as-default-member */
import * as Sentry from '@sentry/react'
import { getForms } from 'api/forms'
import * as pdfFillerApi from 'api/pdfFillerApi'
import axios from 'axios'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import map from 'lodash/map'
import sortBy from 'lodash/sortBy'
import uniqBy from 'lodash/uniqBy'
import * as React from 'react'
import type { ZentakeContextType } from 'types/zentake_context'

import { useAuthContext } from '@/auth/authContext'
import {
  invalidateLoadUserDetails,
  useLoadUserDetails,
} from '@/queries/users/useLoadUsers'

import API from '../api'
import { ALERT_INITIAL_STATE } from './constants'
import { buildFullName, hasText, mapClient } from './helpers'
// TODO: improve typings below and move them to their own files

type Team = {}

type Appointment = {}

type Props = {
  children: React.Node,
}

export const ZentakeContext: React.Context<ZentakeContextType> =
  React.createContext<ZentakeContextType>({
    answerErrors: {},
    clients: [],
    clientInDetail: null,
    fetchingClients: false,
    setTeamDetails: () => undefined,
    fetchingMessages: false,
    fetchingPackets: false,
    fetchingResponses: false,
    fetchingUserDetails: false,
    forms: [],
    getMoreMessages: async () => {},
    getMoreResponses: async () => {},
    getStripeAccountStatus: async () => undefined,
    hasMoreMessages: false,
    hasMoreResponses: false,
    isFetchingForms: false,
    isFetchingPlans: false,
    messages: [],
    packets: [],
    packetToggle: false,
    pdfDocuments: [],
    plans: [],
    tabletModeLockDown: false,
    kioskModeLockDown: false,
    reloadMessages: async () => {},
    responses: [],
    searchClients: async () => {},
    setAlertConfig: () => {},
    setAnswerErrors: () => {},
    setClientInDetail: () => {},
    setPackets: () => {},
    setPacketToggle: () => {},
    setMessages: () => undefined,
    setResponses: () => undefined,
    setTabletModeLockDown: () => undefined,
    setKioskModeLockDown: () => undefined,
    setTeamConfig: () => undefined,
    setGlobalLoading: () => undefined,
    teamDetails: null,
    updateForms: async () => {},
    updatePdfDocuments: async () => {},
    updatePlans: async () => {},
    userDetails: null,
    globalLoading: false,
    updatePackets: async () => undefined,
    updateUserDetails: async () => undefined,
    stripeAccountStatus: null,
    submitFunctions: {
      document: null,
    },
    setSubmitFunctions: () => undefined,
    setClients: () => undefined,
    responseSubmitted: false,
    setResponseSubmitted: () => undefined,
    stripeIntegration: null,
    setStripeIntegration: () => undefined,
    teamConfig: null,
    insuranceCompanies: [],
    fetchInsuranceCompanies: async ({ messageCode, packetMessageCode }) =>
      Promise.resolve(),
    privateFetchInsuranceCompanies: async () => Promise.resolve(),
    isFetchingInsuranceCompanies: false,
  })

function ZentakeContextProvider({ children }: Props): React.Node {
  const { isAuthenticated } = useAuthContext()
  const { data: userDetails } = useLoadUserDetails({
    enabled: !!isAuthenticated,
  })

  const [isOnline, setIsOnline] = React.useState(
    window?.navigator?.onLine ?? true
  )

  const [alertConfig, setAlertConfig] = React.useState(ALERT_INITIAL_STATE)

  const [clientInDetail, setClientInDetail] = React.useState(null)
  const [packetToEdit, setPacketToEdit] = React.useState({})
  const [createExport, setCreateExport] = React.useState(false)
  const [isEditingExport, setIsEditingExport] = React.useState(false)
  const [selectedExport, setSelectedExport] = React.useState(null)
  const [answerErrors, setAnswerErrors] = React.useState({})
  const [, setSentryUser] = React.useState(null)
  const [responseSubmitted, setResponseSubmitted] = React.useState(false)

  const currentTabletModeLockDown =
    window?.localStorage?.getItem?.('tabletModeLockDown')
  const currentKioskModeLockDown =
    window?.localStorage?.getItem?.('kioskModeLockDown')

  const [isFetchingPlans, setFetchingPlans] = React.useState(false)
  const [plans, setPlans] = React.useState([])
  const [forms, setForms] = React.useState([])
  const [isFetchingForms, setFetchingForms] = React.useState(false)
  const [questions, setQuestions] = React.useState([])
  const [packets, setPackets] = React.useState([])
  const [fetchingPackets, setFetchingPackets] = React.useState(false)
  const [clients, setClients] = React.useState([])
  const [clientsLimit, setClientsLimit] = React.useState(100)
  const [clientsAmount, setClientsAmount] = React.useState(0)
  const [currentClientsPageURL, setCurrentClientsPageURL] = React.useState(null)
  const [clientsPage, setClientsPage] = React.useState(1)
  const [nextClientsPageURL, setNextClientsPageURL] = React.useState(null)
  const [prevClientsPageURL, setPrevClientsPageURL] = React.useState(null)
  const [fetchingClients, setFetchingClients] = React.useState(false)
  const [messages, setMessages] = React.useState([])
  const [fetchingMessages, setFetchingMessages] = React.useState(false)
  const [messagesLength, setMessagesLength] = React.useState(
    messages?.length || 0
  )
  const [hasMoreMessages, setHasMoreMessages] = React.useState(true)
  const [responses, setResponses] = React.useState([])
  const [responsesLength, setResponsesLength] = React.useState(
    responses?.length || 0
  )
  const [hasMoreResponses, setHasMoreResponses] = React.useState(true)
  const [fetchingResponses, setFetchingResponses] = React.useState(false)
  const [exports, setExports] = React.useState([])
  const [globalLoading, setGlobalLoading] = React.useState(false)
  const [fetchingUserDetails, setFetchingUserDetails] = React.useState(false)
  const [teamDetails, setTeamDetails] = React.useState(null)
  const [teamConfig, setTeamConfig] = React.useState(null)
  const [tabletModeLockDown, setTabletModeLockDown] = React.useState(
    currentTabletModeLockDown === 'true' || currentTabletModeLockDown === true
  )
  const [kioskModeLockDown, setKioskModeLockDown] = React.useState(
    currentKioskModeLockDown === 'true' || currentKioskModeLockDown === true
  )

  const [stripeIntegration, setStripeIntegration] = React.useState(null)
  const [stripeAccountStatus, setStripeAccountStatus] = React.useState(null)

  const [formTemplates, setFormTemplates] = React.useState([])

  const [socialTeams, setSocialTeams] = React.useState([])
  const [socialTeamsLoading, setSocialTeamsLoading] = React.useState(false)

  const [calendarAppointments, setCalendarAppointments] = React.useState([])
  const [calendarAppointmentsLoading, setCalendarAppointmentsLoading] =
    React.useState(false)
  const [pdfDocuments, setPdfDocuments] = React.useState([])
  const [submitFunctions, setSubmitFunctions] = React.useState({
    document: null,
  })

  const [insuranceCompanies, setInsuranceCompanies] = React.useState([])
  const [isFetchingInsuranceCompanies, setFetchingInsuranceCompanies] =
    React.useState(false)

  const updateOnlineState = React.useCallback(
    () => setIsOnline(window?.navigator?.onLine ?? true),
    []
  )
  React.useEffect(() => {
    window.addEventListener('online', updateOnlineState)
    window.addEventListener('offline', updateOnlineState)
    return () => {
      window.removeEventListener('online', updateOnlineState)
      window.removeEventListener('offline', updateOnlineState)
    }
  }, [updateOnlineState])

  React.useEffect(() => {
    const nextSentryUser = {
      username: buildFullName(userDetails?.first_name, userDetails?.last_name),
      email: userDetails?.username ?? 'no-email',
    }
    setSentryUser((prevSentryUser) => {
      if (isEqual(prevSentryUser, nextSentryUser)) return prevSentryUser
      Sentry.setUser(nextSentryUser)
      return nextSentryUser
    })
  }, [userDetails])

  React.useEffect(() => {
    const prevTabletModeLockDown =
      window?.localStorage?.getItem?.('tabletModeLockDown')
    if (prevTabletModeLockDown !== String(tabletModeLockDown)) {
      window.localStorage.setItem(
        'tabletModeLockDown',
        JSON.stringify(tabletModeLockDown || {})
      )
    }
  }, [tabletModeLockDown])

  window.addEventListener('storage', () => {
    const nextTabletModeLockDown =
      window?.localStorage?.getItem?.('tabletModeLockDown')
    const parsedTabletModeLockDown = nextTabletModeLockDown === 'true'
    if (parsedTabletModeLockDown !== tabletModeLockDown) {
      setTabletModeLockDown(parsedTabletModeLockDown)
    }
  })

  React.useEffect(() => {
    const prevKioskModeLockDown =
      window?.localStorage?.getItem?.('kioskModeLockDown')
    if (prevKioskModeLockDown !== String(kioskModeLockDown)) {
      window.localStorage.setItem(
        'kioskModeLockDown',
        JSON.stringify(kioskModeLockDown || {})
      )
    }
  }, [kioskModeLockDown])

  window.addEventListener('storage', () => {
    const nextKioskModeLockDown =
      window?.localStorage?.getItem?.('kioskModeLockDown')
    const parsedKioskModeLockDown = nextKioskModeLockDown === 'true'
    if (parsedKioskModeLockDown !== kioskModeLockDown) {
      setKioskModeLockDown(parsedKioskModeLockDown)
    }
  })

  const updateMessagesAndResponses = React.useCallback(async () => {
    if (fetchingMessages || fetchingResponses) return

    try {
      setFetchingMessages(true)
      setFetchingResponses(true)
      const [messagesJson, submittedMessagesJson] = await Promise.all([
        API.getMessages(),
        API.getMessagesSubmitted(),
      ])

      // handle pending messages
      const messagesResult = uniqBy(get(messagesJson, 'results', []), 'id')
      const messagesResultLength = messagesResult.length || 0
      setMessagesLength(messagesResultLength)
      setHasMoreMessages(hasText(get(messagesJson, 'next')))
      setMessages(messagesResult)

      // handle submitted responses
      const submittedResponses = uniqBy(
        get(submittedMessagesJson, 'results', []),
        'id'
      )
      const respLength = messagesResult.length || 0
      setResponsesLength(respLength)
      setHasMoreResponses(hasText(get(submittedMessagesJson, 'next')))
      setResponses(submittedResponses)

      setFetchingMessages(false)
      setFetchingResponses(false)
    } catch (error) {
      setFetchingMessages(false)
      setFetchingResponses(false)
    }
  }, [fetchingMessages, fetchingResponses])

  const reloadMessages = React.useCallback(async () => {
    setFetchingMessages(true)
    try {
      const messagesJson = await API.getMessages(messagesLength)
      const messagesResult = uniqBy(get(messagesJson, 'results', []), 'id')
      setMessages(messagesResult)
    } finally {
      setFetchingMessages(false)
    }
  }, [messagesLength])

  const getMoreMessages = React.useCallback(async () => {
    try {
      setFetchingMessages(true)
      const newMessagesResponse = await API.getMoreMessages(messagesLength)
      const newMessages = uniqBy(newMessagesResponse.results, 'id')
      const newMessagesLength = newMessages?.length || 0
      setHasMoreMessages(hasText(get(newMessagesResponse, 'next')))
      setMessagesLength((p) => p + newMessagesLength)

      setMessages((p) => [...p, ...newMessages])
    } catch (error) {
    } finally {
      setFetchingMessages(false)
    }
  }, [messagesLength])

  const getMoreResponses = React.useCallback(async () => {
    try {
      setFetchingResponses(true)
      const newMessagesSubmittedResponse =
        await API.getMoreMessagesSubmitted(responsesLength)
      const newMessagesSubmitted = uniqBy(
        get(newMessagesSubmittedResponse, 'results', []),
        'id'
      )
      const newMessagesSubmittedLength = newMessagesSubmitted.length || 0
      setHasMoreResponses(hasText(get(newMessagesSubmittedResponse, 'next')))
      setResponsesLength((p) => p + newMessagesSubmittedLength)

      setResponses((p) => [...p, ...newMessagesSubmitted])
    } catch (error) {
    } finally {
      setFetchingResponses(false)
    }
  }, [responsesLength])

  const updatePackets = React.useCallback(async () => {
    try {
      setFetchingPackets(true)
      const json = await API.getPackets()
      const result = json?.results || []
      const sortedPackets = sortBy(result, 'name')
      setPackets(sortedPackets)
      setFetchingPackets(false)
    } catch (error) {
      setFetchingMessages(false)
    }
  }, [])

  const syncClientsFetched = React.useCallback((json) => {
    const result = json.results
    const mappedResults = map(result, mapClient)
    setClients(mappedResults)
    let { next } = json
    let { previous } = json

    if (next) {
      next = next.split('v1')[1]
    }

    if (previous) {
      previous = previous.split('v1')[1]
    }

    setNextClientsPageURL(next)
    setPrevClientsPageURL(previous)
  }, [])

  const searchClients = React.useCallback(
    async ({
      search = null,
      order = null,
      nextPage = null,
      prevPage = null,
      paginated = false,
      firstName = null,
      lastName = null,
    }) => {
      let axiosCancel = false

      try {
        setFetchingClients(true)

        let paginatedURL = search || order ? null : currentClientsPageURL

        if (nextPage) {
          paginatedURL = nextClientsPageURL
        } else if (prevPage) {
          paginatedURL = prevClientsPageURL
        }

        const limit = paginated ? 100 : 9999
        const json = await API.getClients({
          paginatedURL,
          search,
          limit,
          order,
          firstName,
          lastName,
        })
        setClientsLimit(limit)
        setClientsAmount(json.count)
        syncClientsFetched(json)
        setCurrentClientsPageURL(paginatedURL)
        setClientsPage(
          paginatedURL ? (paginatedURL.match(/page=(\d+)/) || [0, 1])[1] : 1
        )
        axiosCancel = false
      } catch (error) {
        if (axios.isCancel(error)) {
          axiosCancel = true
        }
      } finally {
        if (!axiosCancel) {
          setFetchingClients(false)
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [
      currentClientsPageURL,
      nextClientsPageURL,
      prevClientsPageURL,
      syncClientsFetched,
    ]
  )

  const updateTeamConfig = React.useCallback(
    async (teamId) => {
      const setVariable = (name: string, value: string) => {
        document.documentElement.style.setProperty(name, value)
      }

      try {
        const json = await API.getTeamConfig(teamId ?? teamDetails?.id)
        const colors = json?.form_appearance

        if (Object.keys(colors).length) {
          const colorMappings = {
            '--page-background-color': colors.page?.background,
            '--form-section-background-color': colors.page?.form,
            '--question-text-color': colors.font?.text,
            '--question-help-text-color': `${colors.font.text}91`,
            '--button-text-color': colors.button?.text,
            '--button-background-color': colors.button?.background,
          }

          Object.entries(colorMappings).forEach(([cssVar, value]) => {
            if (value) setVariable(cssVar, value)
          })
        }

        setTeamConfig(json)
      } catch (error) {
        // do nothing
      }
    },
    [teamDetails]
  )

  React.useEffect(() => {
    async function userDetailsUpdated() {
      setStripeIntegration(userDetails?.team?.stripe_integration)
      if (isNil(userDetails?.team?.id)) return
      const newTeamDetails = await API.getTeam(userDetails.team.id)
      setTeamDetails(newTeamDetails)
      await updateTeamConfig(newTeamDetails.id)
    }
    userDetailsUpdated()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userDetails?.team?.id, userDetails?.team?.stripe_integration])

  const updateUserDetails = React.useCallback(async () => {
    try {
      setFetchingUserDetails(true)
      await invalidateLoadUserDetails()
    } catch (error) {
      // do nothing
    } finally {
      setFetchingUserDetails(false)
    }
  }, [])

  const updateForms = React.useCallback(async () => {
    if (isFetchingForms) return
    try {
      setFetchingForms(true)
      const json = await getForms()
      const formResponse = json.results || []
      const sortedForms = sortBy(formResponse, 'name')
      setForms(sortedForms)
    } catch (error) {
    } finally {
      setFetchingForms(false)
    }
  }, [isFetchingForms])

  const updatePdfDocuments = React.useCallback(async () => {
    try {
      const response = await pdfFillerApi.getPdfDocuments()
      setPdfDocuments(response.results || response) // backwards compatibility
    } catch (err) {
      // do nothing
    }
  }, [])

  const getFormTemplates = React.useCallback(async () => {
    try {
      const json = await API.getTemplates()
      const response = json.results || []
      const sortedTemplates = sortBy(response, 'name')
      setFormTemplates(sortedTemplates)
    } catch (error) {}
  }, [])

  const updatePlans = React.useCallback(async () => {
    try {
      setFetchingPlans(true)
      const json = await API.getPlans()
      const plansResults = json.results || []
      setPlans(plansResults)
    } catch (error) {
    } finally {
      setFetchingPlans(false)
    }
  }, [])

  const getStripeAccountStatus = async () => {
    const response = await API.getStripeAccountStatus()
    setStripeAccountStatus(response.status)
  }

  const getSocialTeams = async () => {
    setSocialTeamsLoading(true)
    try {
      const response = await API.getSocialTeams()
      setSocialTeams(response.results)
    } finally {
      setSocialTeamsLoading(false)
    }
  }

  const createSocialTeam = async (team: Team) => {
    setSocialTeamsLoading(true)
    try {
      const response = await API.createSocialTeam(...team)
      setSocialTeams((previous) => [...previous, response])
    } finally {
      setSocialTeamsLoading(false)
    }
  }

  const updateSocialTeam = async (id: string, team: Team) => {
    setSocialTeamsLoading(true)
    try {
      const response = await API.updateSocialTeam(id, ...team)
      setSocialTeams((previous) =>
        previous.map((t) => (t.id === response.id ? response : t))
      )
    } finally {
      setSocialTeamsLoading(false)
    }
  }

  const deleteSocialTeam = async (id: string) => {
    setSocialTeamsLoading(true)
    try {
      await API.deleteSocialTeam(id)
      setSocialTeams((previous) => previous.filter((t) => t.id !== id))
    } finally {
      setSocialTeamsLoading(false)
    }
  }

  const getCalendarAppointments = async () => {
    setCalendarAppointmentsLoading(true)
    try {
      const response = await API.getAppointments()
      setCalendarAppointments(response.results)
    } finally {
      setCalendarAppointmentsLoading(false)
    }
  }

  const createCalendarAppointment = async (appointment: Appointment) => {
    setCalendarAppointmentsLoading(true)
    try {
      const response = await API.createAppointment(appointment)
      setCalendarAppointments((previous) => [...previous, response])
    } finally {
      setCalendarAppointmentsLoading(false)
    }
  }

  const updateCalendarAppointment = async (
    id: string,
    appointment: Appointment
  ) => {
    setCalendarAppointmentsLoading(true)
    try {
      const response = await API.updateAppointment(id, appointment)
      setCalendarAppointments((previous) =>
        previous.map((t) => (t.id === response.id ? response : t))
      )
    } finally {
      setCalendarAppointmentsLoading(false)
    }
  }

  const deleteCalendarAppointment = async (id: string) => {
    setCalendarAppointmentsLoading(true)
    try {
      await API.deleteAppointment(id)
      setCalendarAppointments((previous) => previous.filter((t) => t.id !== id))
    } finally {
      setCalendarAppointmentsLoading(false)
    }
  }

  const fetchInsuranceCompanies = React.useCallback(
    async ({
      messageCode,
      packetMessageCode,
    }: {
      messageCode: string,
      packetMessageCode?: ?string,
    }) => {
      try {
        setFetchingInsuranceCompanies(true)
        const response = await API.listPublicInsuranceCompanies({
          messageCode,
          packetMessageCode,
        })
        setInsuranceCompanies(
          response.sort((a, b) => a.name.localeCompare(b.name))
        )
      } finally {
        setFetchingInsuranceCompanies(false)
      }
    },
    []
  )

  const privateFetchInsuranceCompanies = React.useCallback(async () => {
    try {
      setFetchingInsuranceCompanies(true)
      const response = await API.listInsuranceCompanies()
      setInsuranceCompanies(
        response.sort((a, b) => a.name.localeCompare(b.name))
      )
    } finally {
      setFetchingInsuranceCompanies(false)
    }
  }, [])

  const defaultContext = {
    alertConfig,
    answerErrors,
    calendarAppointments,
    calendarAppointmentsLoading,
    clientInDetail,
    clients,
    clientsAmount,
    clientsLimit,
    clientsPage,
    createCalendarAppointment,
    createExport,
    createSocialTeam,
    deleteCalendarAppointment,
    deleteSocialTeam,
    exports,
    fetchingClients,
    fetchingMessages,
    fetchingPackets,
    fetchingResponses,
    fetchingUserDetails,
    formTemplates,
    forms,
    getCalendarAppointments,
    getFormTemplates,
    getMoreMessages,
    getMoreResponses,
    getSocialTeams,
    getStripeAccountStatus,
    hasClientsNextPage: !!nextClientsPageURL,
    hasClientsPrevPage: !!prevClientsPageURL,
    hasMoreMessages,
    hasMoreResponses,
    isEditingExport,
    isFetchingForms,
    isFetchingPlans,
    isOnline,
    messages,
    packetToEdit,
    packets,
    pdfDocuments,
    plans,
    questions,
    reloadMessages,
    responses,
    responseSubmitted,
    setResponseSubmitted,
    searchClients,
    selectedExport,
    setAlertConfig,
    setAnswerErrors,
    setClientInDetail,
    setClients,
    setCreateExport,
    setExports,
    setFormTemplates,
    setForms,
    setIsEditingExport,
    setMessages,
    setPacketToEdit,
    setPackets,
    setPlans,
    setQuestions,
    setResponses,
    setSelectedExport,
    setStripeIntegration,
    setTabletModeLockDown,
    setKioskModeLockDown,
    setTeamConfig,
    setTeamDetails,
    setGlobalLoading,
    socialTeams,
    socialTeamsLoading,
    stripeIntegration,
    stripeAccountStatus,
    tabletModeLockDown,
    kioskModeLockDown,
    teamConfig,
    teamDetails,
    updateCalendarAppointment,
    updateForms,
    updateMessagesAndResponses,
    updatePackets,
    updatePdfDocuments,
    updatePlans,
    updateSocialTeam,
    updateTeamConfig,
    updateUserDetails,
    userDetails,
    globalLoading,
    submitFunctions,
    setSubmitFunctions,
    insuranceCompanies,
    isFetchingInsuranceCompanies,
    fetchInsuranceCompanies,
    privateFetchInsuranceCompanies,
  }

  return (
    <ZentakeContext.Provider value={defaultContext}>
      {children}
    </ZentakeContext.Provider>
  )
}

export default ZentakeContextProvider
